/**
 * OKS-739 — Single source of truth for the creator app-column width.
 *
 * The creator app is mobile-first: its only primary navigation is the fixed
 * bottom `<TabBar>`. Before this ticket the bar (and its reserved padding, and
 * the mission-detail CTA offset) were force-hidden/reset at `lg` (≥1024px),
 * leaving iPad creators with a nav-less page. We keep the bottom-tab shell at
 * ALL widths and, on wide screens only, constrain the app to a centered column
 * so it reads as intentional rather than a stretched phone (TRD KD-1/KD-2).
 *
 * This column width is shared by THREE surfaces that must never diverge (a
 * divergence would misalign the nav, the content, or the CTA against each
 * other — PRD Core Rule):
 *   - `creator-shell.tsx` `<main>` — the content column.
 *   - `tab-bar.tsx` — the bottom-nav tab row (the bar background stays
 *     full-bleed; only the tabs center to this column).
 *   - `mission-cta-bar.tsx` — the sticky mission CTA above the bar.
 *
 * Keeping the class literal HERE, once, is what makes the "one edit point"
 * guarantee real: Tailwind v4's JIT scans source for arbitrary-value utilities
 * (`max-w-[480px]`), so the literal has to live somewhere it can see it, and
 * surfaces import the symbol rather than re-typing the classes — none can
 * silently drift to a different width. LQE's Playwright matrix asserts the
 * three surfaces share the same centered column edge at 1366px.
 *
 * `lg:`-prefixed so it is inert below 1024px: phone (320–767) and small-iPad
 * portrait (768/810/834, which already showed the bar) stay full-width,
 * byte-identical to pre-OKS-739 (TRD KD-4).
 *
 * The 480px value matches the established creator reading column already used
 * by `MissionDetail` / `MissionCtaBar`.
 *
 * Two forms are exported because the surfaces gate centering differently but
 * MUST share the same width:
 *
 *   - `CREATOR_APP_COLUMN` — `lg:`-gated. For surfaces that are full-bleed on
 *     mobile and only center on wide screens: `creator-shell` `<main>` and the
 *     `tab-bar` tab row. Inert below 1024px (phone + small-iPad portrait
 *     unchanged, TRD KD-4).
 *   - `CREATOR_APP_COLUMN_STICKY` — un-gated. For the mobile sticky
 *     `mission-cta-bar` inner, which caps to the reading column at ALL widths
 *     (existing OKS-699 behavior; it stays a fixed bar above the nav on wide
 *     screens too, so it centers to the same column there).
 *
 * Tailwind v4's JIT scans source for the arbitrary-value literal
 * (`max-w-[480px]`), so it CANNOT be composed via template strings — both
 * forms spell it out. To retarget the app-column width, edit the `[480px]` in
 * BOTH literals below (this file is still the single edit point — every
 * surface imports a symbol, none re-types the class).
 */
export const CREATOR_APP_COLUMN = "lg:mx-auto lg:w-full lg:max-w-[480px]"

/** All-width variant — see module note (`mission-cta-bar` sticky inner). */
export const CREATOR_APP_COLUMN_STICKY = "mx-auto w-full max-w-[480px]"
